/** Analytics Portal V1 — Summary category filter, comparison toggle, refresh action and the
 * archived indicator (Phase 9, plan §12.1). Small shared controls, one file. */
import React from "react";
import { Archive, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

const CATEGORY_OPTIONS = [
  { key: "all", label: "All" },
  { key: "personal", label: "Personal" },
  { key: "business", label: "Business" },
  { key: "capital", label: "Capital" },
  { key: "event", label: "Events" },
  { key: "resource", label: "Resources" },
];

export const CategoryFilter = ({ value = "all", onChange, counts = {}, disabled }) => (
  <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by entity type">
    {CATEGORY_OPTIONS.map((option) => {
      const active = option.key === value;
      const count = option.key === "all" ? null : counts?.[option.key];
      return (
        <button
          key={option.key} type="button" disabled={disabled} aria-pressed={active}
          onClick={() => onChange(option.key)}
          className={`rounded-full border px-3 py-1 text-xs font-[560] transition-colors disabled:opacity-50 ${
            active ? "border-primary bg-primary text-primary-foreground"
              : "border-border bg-card text-muted-foreground hover:text-foreground"}`}
          data-testid={`analytics-category-${option.key}`}
        >
          {option.label}{count ? ` (${count})` : ""}
        </button>
      );
    })}
  </div>
);

export const ComparisonToggle = ({ checked, onChange, comparisonLabel, disabled }) => (
  <div className="flex items-center gap-2">
    <Switch id="analytics-compare" checked={checked} onCheckedChange={onChange}
            disabled={disabled} data-testid="analytics-compare-toggle" />
    <Label htmlFor="analytics-compare" className="cursor-pointer text-xs text-muted-foreground">
      {comparisonLabel && checked ? comparisonLabel : "Compare with previous period"}
    </Label>
  </div>
);

export const RefreshButton = ({ onRefresh, busy, disabled }) => (
  <Button variant="ghost" size="sm" onClick={onRefresh} disabled={disabled || busy}
          aria-label="Refresh analytics" data-testid="analytics-refresh">
    <RotateCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
    <span className="ml-1.5 hidden sm:inline">{busy ? "Refreshing…" : "Refresh"}</span>
  </Button>
);

export const ArchivedNotice = ({ entity }) => {
  if (!entity?.archived) return null;
  return (
    <p className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 px-3 py-2 text-xs text-muted-foreground"
       data-testid="analytics-archived-notice" role="note">
      <Archive className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      This entity is archived. Historical analytics remain available and no new activity is expected.
    </p>
  );
};
